import { CampaignConfigSchema } from '../types';
import { typeHasMessageTemplates } from '../registry/sectionRegistry';
import { validateRecurringTemplateSettings } from './recurringTemplateHelpers';
import { validateTextToWinTemplateSettings } from './textToWinTemplateHelpers';
import { validateEnrollmentTemplateSettings } from './enrollmentTemplateHelpers';

/**
 * Per-type validation errors keyed by field path (e.g. 'recurringSettings.dateEnd').
 * Empty object means the type-specific settings are valid.
 */
export const getTypeSpecificErrors = (config: CampaignConfigSchema): Record<string, string> => {
  if (config.type === 'recurring') {
    return validateRecurringTemplateSettings(config.recurringSettings);
  }
  if (config.type === 'text_to_win') {
    return validateTextToWinTemplateSettings(config.textToWinSettings);
  }
  if (config.type === 'enrollment') {
    return validateEnrollmentTemplateSettings(config.enrollmentSettings);
  }
  return {};
};

/**
 * Human-readable list of validation errors for the save tooltip.
 * Ported from the original CampaignConfigBuilder.tsx validationErrors memo,
 * extended with per-type settings errors.
 */
export const getValidationErrors = (config: CampaignConfigSchema): string[] => {
  const errors: string[] = [];
  if (!config.name || config.name.trim() === '') errors.push('Name is required');
  if (!config.description || config.description.trim() === '') errors.push('Description is required');
  if (config.type === 'custom') errors.push('Please select a campaign type');
  if (typeHasMessageTemplates(config.type) && config.messageTemplates.length === 0)
    errors.push('At least one message template is required');
  errors.push(...Object.values(getTypeSpecificErrors(config)));
  return errors;
};

/**
 * Whether the config can be saved.
 * Ported from the original isConfigValid memo, extended with per-type rules.
 */
export const isConfigValid = (config: CampaignConfigSchema): boolean => {
  if (config.category === '') return false;
  if (config.type === 'custom') return false;
  if (!config.name || config.name.trim() === '') return false;
  if (!config.description || config.description.trim() === '') return false;
  if (typeHasMessageTemplates(config.type) && config.messageTemplates.length === 0) return false;
  if (Object.keys(getTypeSpecificErrors(config)).length > 0) return false;
  return true;
};
